import React, { useState, useEffect } from 'react';
import { Users, FileText, Key, Trash2, Download, Plus, Eye, EyeOff, LogIn, BarChart3, TrendingUp, TrendingDown, UserCheck, Database, AlertTriangle, PlayCircle, Clock, MessagesSquare } from 'lucide-react';
import axiosAdmin from './AdminAxios.jsx';
import SalleAdmin from './salle/SalleAdmin.jsx';

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("adminToken"));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [initDbLoading, setInitDbLoading] = useState(false);
  const [initDbLogs, setInitDbLogs] = useState([]);
  const [showInitDbModal, setShowInitDbModal] = useState(false);
  const [migrations, setMigrations] = useState([]);
  const [migrationsLoading, setMigrationsLoading] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [selectedMigration, setSelectedMigration] = useState(null);
  const [migrationLogs, setMigrationLogs] = useState([]);
  const [migrationLoading, setMigrationLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const [ovRes, usersRes, filesRes] = await Promise.all([
        axiosAdmin.get('/admin/overview'),
        axiosAdmin.get('/admin/users'),
        axiosAdmin.get('/admin/files')
      ]);
      setOverview(ovRes.data);
      setUsers(usersRes.data);
      setFiles(filesRes.data);
      setError(null);
    } catch (err) {
      setError('Erreur de chargement des données');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchMigrations = async () => {
    setMigrationsLoading(true);
    try {
      const res = await axiosAdmin.get('/admin/migrations');
      setMigrations(res.data.migrations || []);
    } catch (err) {
      setError('Erreur de chargement des migrations');
    } finally {
      setMigrationsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'database') {
      fetchMigrations();
    }
  }, [activeTab]);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosAdmin.post('/admin/login', { username, password });
      localStorage.setItem('adminToken', res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError('Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  const addApiKey = async (userId) => {
    const apiKey = prompt('Nouvelle clé API :');
    if (!apiKey) return;
    try {
      await axiosAdmin.post(`/admin/users/${userId}/api-key`, { apiKey });
      await fetchData();
    } catch (err) {
      setError('Erreur lors de l\'ajout de la clé API');
    }
  };

  const removeApiKey = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette clé API ?')) return;
    try {
      await axiosAdmin.delete(`/admin/users/${userId}/api-key`);
      await fetchData();
    } catch (err) {
      setError('Erreur lors de la suppression de la clé API');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce compte ? Cette action est irréversible.')) return;
    try {
      await axiosAdmin.delete(`/admin/users/${userId}`);
      await fetchData();
    } catch (err) {
      setError('Erreur lors de la suppression du compte');
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axiosAdmin.get(`/admin/download/${file}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      setError('Erreur lors du téléchargement');
    }
  };

  const executeInitDb = async () => {
    setInitDbLoading(true);
    setInitDbLogs([]);
    setShowInitDbModal(true);

    try {

      const res = await axiosAdmin.post('/admin/init-db');
      setInitDbLogs(res.data.logs);

      // Recharger les données après l'initialisation
      await fetchData();
    } catch (err) {
      setInitDbLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: 'Erreur lors de l\'initialisation: ' + err.message,
        type: 'error'
      }]);
      setError('Erreur lors de l\'exécution d\'init_db.py');
    } finally {
      setInitDbLoading(false);
    }
  };

  const executeMigration = async () => {
    if (!selectedMigration) return;

    setMigrationLoading(true);
    setMigrationLogs([]);

    try {
      const res = await axiosAdmin.post(`/admin/migrations/${selectedMigration.name}`);
      setMigrationLogs(res.data.logs);

      // Recharger les données après la migration
      await fetchData();
      await fetchMigrations();
    } catch (err) {
      setMigrationLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: 'Erreur lors de l\'exécution: ' + err.message,
        type: 'error'
      }]);
      setError('Erreur lors de l\'exécution de la migration');
    } finally {
      setMigrationLoading(false);
    }
  };

  const calculateWinRate = (won, total) => {
    return total > 0 ? ((won / total) * 100).toFixed(1) : 0;
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-indigo-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <LogIn className="h-8 w-8 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Administration</h2>
            <p className="text-gray-600">Connectez-vous pour accéder au tableau de bord</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom d'utilisateur
              </label>
              <input
                className="w-full border text-black border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Entrez votre nom d'utilisateur"
                value={username}
                onChange={e => setUsername(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  className="w-full border text-black border-gray-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  type={showPassword ? "text" : "password"}
                  placeholder="Entrez votre mot de passe"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                'Se connecter'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord administrateur</h1>
            <div className="flex items-center space-x-4">
              <a href="/conference" className="text-indigo-600 hover:text-indigo-900 font-medium px-3 py-2 rounded-md hover:bg-indigo-50 transition-colors">
                Mode Conférence
              </a>
              <a href="/conference/seminaire" className="text-indigo-600 hover:text-indigo-900 font-medium px-3 py-2 rounded-md hover:bg-indigo-50 transition-colors">
                Conférence Séminaire
              </a>
              <button
                onClick={() => {
                  setToken(null);
                  localStorage.removeItem('adminToken');
                }}
                className="text-gray-500 hover:text-gray-700 font-medium"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-600 text-sm">{error}</div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
              { id: 'users', label: 'Utilisateurs', icon: Users },
              { id: 'files', label: 'Fichiers', icon: FileText },
              { id: 'database', label: 'Base de données', icon: Database },
              // Les questions du public des conferences : archiver entre deux
              // seances, relire les anciennes. Voir salle/SalleAdmin.jsx.
              { id: 'salle', label: 'Salle', icon: MessagesSquare }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {dataLoading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && !dataLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Parties jouées</p>
                  <p className="text-2xl font-bold text-gray-900">{overview.games?.toLocaleString()}</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Parties gagnées</p>
                  <p className="text-2xl font-bold text-green-600">{overview.wins?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">
                    {calculateWinRate(overview.wins, overview.games)}% de victoires
                  </p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Parties perdues</p>
                  <p className="text-2xl font-bold text-red-600">{overview.losses?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">
                    {calculateWinRate(overview.losses, overview.games)}% de défaites
                  </p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Comptes créés</p>
                  <p className="text-2xl font-bold text-gray-900">{overview.users?.toLocaleString()}</p>
                </div>
                <div className="bg-purple-100 rounded-full p-3">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Utilisateurs avec clé API</p>
                  <p className="text-2xl font-bold text-gray-900">{overview.api_key_users?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">
                    {overview.users > 0 ? ((overview.api_key_users / overview.users) * 100).toFixed(1) : 0}% des utilisateurs
                  </p>
                </div>
                <div className="bg-indigo-100 rounded-full p-3">
                  <UserCheck className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && !dataLoading && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Gestion des utilisateurs</h3>
              <p className="text-sm text-gray-500 mt-1">{users.length} utilisateur(s) enregistré(s)</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statistiques
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Année actuelle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clé API
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.games_played} parties
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.games_won} victoires • {user.games_lost} défaites
                        </div>
                        <div className="text-xs text-gray-400">
                          Taux de victoire: {calculateWinRate(user.games_won, user.games_played)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.current_year ? (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-blue-500 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{user.current_year}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.has_api_key
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {user.has_api_key ? 'Configurée' : 'Non configurée'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => addApiKey(user.id)}
                          className="inline-flex items-center px-3 py-1 border border-blue-300 rounded text-blue-600 hover:bg-blue-50 text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {user.has_api_key ? 'Modifier' : 'Ajouter'} clé
                        </button>
                        {user.has_api_key && (
                          <button
                            onClick={() => removeApiKey(user.id)}
                            className="inline-flex items-center px-3 py-1 border border-orange-300 rounded text-orange-600 hover:bg-orange-50 text-xs"
                          >
                            <Key className="h-3 w-3 mr-1" />
                            Supprimer clé
                          </button>
                        )}
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="inline-flex items-center px-3 py-1 border border-red-300 rounded text-red-600 hover:bg-red-50 text-xs"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && !dataLoading && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Fichiers de parties</h3>
              <p className="text-sm text-gray-500 mt-1">{files.length} fichier(s) disponible(s)</p>
            </div>
            <div className="divide-y divide-gray-200">
              {files.map(file => (
                <div key={file} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{file}</div>
                      <div className="text-sm text-gray-500">
                        Fichier de partie • {new Date().toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadFile(file)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </button>
                </div>
              ))}
              {files.length === 0 && (
                <div className="px-6 py-8 text-center text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun fichier disponible</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Database Tab */}
        {activeTab === 'database' && !dataLoading && (
          <div className="space-y-6">
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Gestion de la base de données</h3>
                <p className="text-sm text-gray-500 mt-1">Outils d'administration et de maintenance</p>
              </div>

              <div className="p-6 space-y-6">
                {/* Init DB Section */}
                <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">
                        Réinitialisation de la base de données
                      </h4>
                      <p className="text-sm text-yellow-700 mb-4">
                        Cette action va exécuter le script <code className="bg-yellow-100 px-1 rounded">init_db.py</code> qui
                        réinitialisera complètement la base de données.
                        <strong className="block mt-1">⚠️ Toutes les données existantes seront perdues !</strong>
                      </p>
                      <button
                        onClick={() => setShowInitDbModal(true)}
                        disabled={initDbLoading}
                        className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Database className="h-4 w-4 mr-2" />
                        {initDbLoading ? 'Exécution en cours...' : 'Exécuter init_db.py'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Migrations Section */}
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-start">
                    <PlayCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">
                        Scripts de migration
                      </h4>
                      <p className="text-sm text-blue-700 mb-4">
                        Exécutez des scripts de migration pour mettre à jour la structure de la base de données.
                      </p>

                      {migrationsLoading ? (
                        <div className="flex items-center text-blue-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                          Chargement des migrations...
                        </div>
                      ) : migrations.length > 0 ? (
                        <div className="space-y-2">
                          {migrations.map(migration => (
                            <div key={migration.name} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-200">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{migration.name}</div>
                                <div className="text-xs text-gray-500">
                                  {migration.type === 'python' ? '🐍 Python' : '📊 SQL'} • {(migration.size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedMigration(migration);
                                  setShowMigrationModal(true);
                                  setMigrationLogs([]);
                                }}
                                className="inline-flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
                              >
                                <PlayCircle className="h-3 w-3 mr-1" />
                                Exécuter
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-blue-600">Aucun script de migration disponible</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Database Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-600">État de la DB</div>
                    <div className="text-lg font-semibold text-green-600 mt-1">Opérationnelle</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-600">Dernière sauvegarde</div>
                    <div className="text-lg font-semibold text-gray-900 mt-1">Aujourd'hui</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-600">Taille DB</div>
                    <div className="text-lg font-semibold text-gray-900 mt-1">~2.4 MB</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Init DB Modal */}
        {showInitDbModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Terminal className="h-5 w-5 mr-2" />
                  Exécution d'init_db.py
                </h3>
                {!initDbLoading && (
                  <button
                    onClick={() => setShowInitDbModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="p-6">
                {!initDbLoading && initDbLogs.length === 0 && (
                  <div className="mb-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800 mb-1">
                            Attention - Action irréversible
                          </h4>
                          <p className="text-sm text-red-700">
                            Cette action va supprimer toutes les données existantes et recréer la base de données
                            avec sa structure initiale. Les utilisateurs, parties et fichiers seront perdus.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                      Êtes-vous sûr de vouloir continuer ?
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={executeInitDb}
                        className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <Database className="h-4 w-4 mr-2" />
                        Oui, réinitialiser la DB
                      </button>
                      <button
                        onClick={() => setShowInitDbModal(false)}
                        className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium rounded-lg transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Logs Console */}
                {(initDbLoading || initDbLogs.length > 0) && (
                  <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-auto max-h-96">
                    <div className="text-green-400 mb-2">$ python init_db.py</div>
                    {initDbLogs.map((log, index) => (
                      <div key={index} className="mb-1">
                        <span className="text-gray-500">[{log.timestamp}]</span>{' '}
                        <span className={
                          log.type === 'error' ? 'text-red-400' :
                            log.type === 'success' ? 'text-green-400' :
                              'text-white'
                        }>
                          {log.message}
                        </span>
                      </div>
                    ))}
                    {initDbLoading && (
                      <div className="flex items-center text-yellow-400">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-400 border-t-transparent mr-2"></div>
                        Exécution en cours...
                      </div>
                    )}
                  </div>
                )}

                {!initDbLoading && initDbLogs.length > 0 && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => {
                        setShowInitDbModal(false);
                        setInitDbLogs([]);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Migration Modal */}
        {showMigrationModal && selectedMigration && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <PlayCircle className="h-5 w-5 mr-2" />
                  Exécution de {selectedMigration.name}
                </h3>
                {!migrationLoading && (
                  <button
                    onClick={() => {
                      setShowMigrationModal(false);
                      setSelectedMigration(null);
                      setMigrationLogs([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="p-6">
                {!migrationLoading && migrationLogs.length === 0 && (
                  <div className="mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800 mb-1">
                            Confirmation requise
                          </h4>
                          <p className="text-sm text-blue-700">
                            Vous êtes sur le point d'exécuter le script <strong>{selectedMigration.name}</strong>.
                            Cette action peut modifier la structure de la base de données.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-gray-600 mb-4">
                      Voulez-vous continuer ?
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={executeMigration}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Oui, exécuter la migration
                      </button>
                      <button
                        onClick={() => {
                          setShowMigrationModal(false);
                          setSelectedMigration(null);
                        }}
                        className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium rounded-lg transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Logs Console */}
                {(migrationLoading || migrationLogs.length > 0) && (
                  <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-auto max-h-96">
                    <div className="text-green-400 mb-2">$ Exécution de {selectedMigration.name}</div>
                    {migrationLogs.map((log, index) => (
                      <div key={index} className="mb-1">
                        <span className="text-gray-500">[{log.timestamp}]</span>{' '}
                        <span className={
                          log.type === 'error' ? 'text-red-400' :
                            log.type === 'success' ? 'text-green-400' :
                              'text-white'
                        }>
                          {log.message}
                        </span>
                      </div>
                    ))}
                    {migrationLoading && (
                      <div className="flex items-center text-yellow-400">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-400 border-t-transparent mr-2"></div>
                        Exécution en cours...
                      </div>
                    )}
                  </div>
                )}

                {!migrationLoading && migrationLogs.length > 0 && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => {
                        setShowMigrationModal(false);
                        setSelectedMigration(null);
                        setMigrationLogs([]);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'salle' && !dataLoading && <SalleAdmin />}
      </div>
    </div>
  );
};

export default AdminPage;